/* Profile-photo uploader used on the vendor application form. */
import { useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfilePictureField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputId = "profile-picture-input";

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast("Please choose an image file", { duration: 2800 });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast("Image must be under 8MB", { duration: 2800 });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await axios.post(`${API}/uploads/vendor-media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.files?.[0]?.url;
      if (!url) throw new Error("No URL returned");
      onChange(url);
      toast("Profile picture uploaded", { duration: 2200 });
    } catch (err) {
      toast("Upload failed", {
        description: err?.response?.data?.detail || "Please try again.",
        duration: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid="profile-picture-field"
      className="flex items-center gap-5"
    >
      <div
        className="relative h-24 w-24 shrink-0 overflow-hidden border border-[var(--ld-ink)]/30 bg-white"
        aria-hidden
      >
        {value ? (
          <img
            data-testid="profile-picture-preview"
            src={
              value.startsWith("http")
                ? value
                : `${process.env.REACT_APP_BACKEND_URL}${value}`
            }
            alt="Profile preview"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[var(--ld-ink-muted)]">
            <Upload size={20} strokeWidth={1.4} />
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <label
            htmlFor={inputId}
            data-testid="profile-picture-upload-button"
            className={
              "inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase border cursor-pointer transition-colors duration-300 " +
              (uploading
                ? "border-[var(--ld-ink)]/20 text-[var(--ld-ink-muted)] cursor-wait"
                : "border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)]")
            }
          >
            <Upload size={13} strokeWidth={1.6} />
            {uploading
              ? "Uploading…"
              : value
              ? "Replace photo"
              : "Choose photo"}
          </label>
          {value && (
            <button
              type="button"
              data-testid="profile-picture-remove"
              onClick={() => onChange("")}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/20 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] hover:border-[var(--ld-ink)]/40 transition-colors duration-300"
            >
              <X size={12} strokeWidth={1.6} />
              Remove
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-[var(--ld-ink-muted)]/80 leading-relaxed">
          Square photo works best. JPG/PNG/WEBP, up to 8MB.
        </p>
        <input
          id={inputId}
          data-testid="profile-picture-input"
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};
